import {api} from "../../../../../api/API";
import {LogiCtrlExistingItem} from "../../../../../api/LogiCtrl";
import {
    AdjustCol,
    NumberCol, RenderStringCol2, SelectInput,
    StringCol,
    Table,
    useTableRefresher
} from "../../../../../misc/scroller/Table";
import {StatusCol} from "../Openings";
import {Button} from "@mui/material";
import {useBackgroundAction} from "../../../manage/sov/UseBackgroundTask";
import {byFixedOrder} from "../../pricing/Pricing";
import {green, grey, red} from "@mui/material/colors";
import {UserExistingInstructions} from "./UserRuleInstructions";
import {useSnackAction} from "../../hardware/SnackAction";
import {useState} from "react";
import {blue, orange} from "@mui/material/colors";
import {PoModifyReviewDialog} from "../../logictrl/PoModifyReviewDialog";
import {PoReviewCol, PoCol, poModifyRowColor} from "../../logictrl/PoModifyColumns";

export const existingActionOptions = [
    {display: "Choose Action", value: "-"},
    {display: "Leave", value: "skip"},
    {display: "Return", value: "return"},
    {display: "Recalculate", value: "not-released-replace"},
];

// keep in sync with AllowedExistingActions() in core/logistics/logictrl/tlogictrl
export function existingActionOptionsForStatus(status: string) {
    switch (status) {
        case "not-released":
        case "released":
        case "po-draft":
            return existingActionOptions.filter(o => o.value === "not-released-replace");
        case "pending-receiving":
            return existingActionOptions.filter(o => o.value === "skip");
        default:
            return existingActionOptions.filter(o => o.value !== "not-released-replace");
    }
}

// ordered but not at the site yet: Leave means the order continues, not "leave on site"
export function isInFlightStatus(status: string) {
    switch (status) {
        case "ordered":
        case "back-order":
        case "ready-for-pickup":
        case "pending-receiving":
        case "in-warehouse":
            return true;
        default:
            return false;
    }
}

export function findActionDisplay(value: string | null) {
    const found = existingActionOptions.find(v => v.value === value);
    if(found) return found.display;

    return `unknown: '${value}'`;
}

export const leftBySkipDisplay = "Leave (skip chosen on products side)";

// a stored default is shown as such; the review popover asks for a decision in that case
export function existingActionDisplay(v: LogiCtrlExistingItem) {
    if(v.leftBySkip) return leftBySkipDisplay;
    if(v.autoReplace) return "Recalculate";
    if(v.actionIsDefault && v.action && v.action !== "-") return `${findActionDisplay(v.action)} (default)`;

    return v.action ? findActionDisplay(v.action) : "Leave";
}

export function OpeningsExistingTab(props: {
    opening: number;
    project: number;
    div10?: number;
    miscProduct?: number;
    glazing?: number;
    onNeedsRecalc(tf: boolean): void;
    onReload?(): void;
}) {
    const tableName = "logictrl-existing";
    const refresh = useTableRefresher(tableName);

    const apply = useBackgroundAction<Parameters<typeof api.logiCtrl.applyExisting>[0], any>({
        callback: async (input) => api.logiCtrl.applyExisting(input),
        onDone: () => {
            props.onNeedsRecalc(false);
            props.onReload?.();
            refresh();
        }
    });

    const recalculate = useSnackAction(async (input: {project: number; openings: number[]}) => {
        await api.openings.recalculate(input);
        props.onNeedsRecalc(false);
        props.onReload?.();
        refresh();
    }, {
        success: "Recalculated",
    });

    const [review, setReview] = useState<LogiCtrlExistingItem | null>(null);

    const clearApproval = useBackgroundAction<Parameters<typeof api.logiCtrl.setPoModifyApproval>[0], any>({
        callback: async (input) => api.logiCtrl.setPoModifyApproval(input),
        onDone: () => {
            props.onReload?.();
            refresh();
        }
    });

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
        }}>
            {review && <PoModifyReviewDialog project={props.project} item={review} onClose={(approved) => {
                setReview(null);
                if(approved) {
                    props.onReload?.();
                    refresh();
                }
            }} />}
            <div style={{
                display: "flex",
                minHeight: 250,
                minWidth: 500
            }}>
                <Table<LogiCtrlExistingItem>
                    name={tableName}
                    cellCustomize={{
                        backgroundColor: (row: LogiCtrlExistingItem) => {
                            const poColor = poModifyRowColor(row, blue["50"], orange["100"]);
                            if(poColor) return poColor;
                            if(row.errorStr) return red["50"]
                            if(row.action === "skip" || row.leftBySkip) return grey["100"];
                            if(row.action === "return") return green["100"];
                            return null;
                        }
                    }}
                    columns={[
                        StringCol("Name", "name", 200),
                        NumberCol("Qty", "qty", 50),
                        StatusCol<LogiCtrlExistingItem>("Status", "status"),
                        PoCol(),
                        PoReviewCol({
                            onReview: setReview,
                            clearing: clearApproval.loading,
                            onClear: (v) => clearApproval.callback({
                                project: props.project,
                                ids: [v.id],
                                approved: false,
                            }),
                        }),
                        AdjustCol(RenderStringCol2<LogiCtrlExistingItem>("Action", existingActionDisplay, v => {
                            if(v.leftBySkip || v.autoReplace || !v.action || v.actionIsDefault) {
                                return <i>{existingActionDisplay(v)}</i>
                            }

                            return existingActionDisplay(v)
                        }), {
                            editKey: "action",
                            editable: {
                                type: "select",
                                options: ((input: SelectInput<LogiCtrlExistingItem>) =>
                                    existingActionOptionsForStatus(input.props("status").status) as any) as any,
                                getValue: existingActionDisplay,
                                freeSolo: false,
                            }
                        }),
                        StringCol("Error", "error", 200),
                    ]}
                    fetch={async () => {
                        return await api.logiCtrl.listExisting({opening: props.opening || undefined, div10: props.div10, miscProduct: props.miscProduct, glazing: props.glazing, project: props.project});
                    }}
                    sort={(a, b) => {
                        return sorter(a.kind, b.kind)
                    }}
                    fetchDeps={[props.opening, props.div10, props.miscProduct, props.glazing]}
                    onChange={async (input) => {
                        props.onNeedsRecalc(true);
                        return api.logiCtrl.upsertExisting(input);
                    }}
                    noPersonalizeColViz
                />
            </div>
            <div style={{display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 4, padding: 12}}>
                {apply.LoadingElement}
                {props.opening > 0 && <Button size="small" variant="outlined" disabled={recalculate.loading} onClick={() => recalculate.callback({
                    project: props.project,
                    openings: [props.opening],
                })} sx={{textTransform: "none", fontSize: 12}}>
                    Recalculate
                </Button>}
                <Button size="small" variant="contained" onClick={() => apply.callback({
                    opening: props.opening || undefined,
                    div10: props.div10,
                    miscProduct: props.miscProduct,
                    glazing: props.glazing,
                    project: props.project,
                })} sx={{textTransform: "none", fontSize: 12}}>
                    Apply Changes
                </Button>
            </div>
            <UserExistingInstructions />
        </div>
    );
}

const sorter = byFixedOrder(["door", "frame", "frame-anchor", "hardware"])